import { create } from 'zustand'

type Theme = 'light' | 'dark'
/** Lựa chọn của người dùng — "system" bám theo cài đặt sáng/tối của máy. */
export type ThemePref = Theme | 'system'

interface UiState {
  /** Giao diện đang áp dụng thực tế (đã quy "system" về sáng/tối). */
  theme: Theme
  themePref: ThemePref
  sidebarCollapsed: boolean
  toggleTheme: () => void
  setTheme: (t: Theme) => void
  setThemePref: (p: ThemePref) => void
  toggleSidebar: () => void
  setSidebarPinned: (pinned: boolean) => void
  /** Bảng lệnh ⌘K (components/shared/command-palette.tsx) đang mở — không lưu lại. */
  commandOpen: boolean
  setCommandOpen: (open: boolean) => void
}

const THEME_KEY = 'ecdp_theme'
const media = window.matchMedia('(prefers-color-scheme: dark)')

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY)
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
  } catch {
    return 'system'
  }
}

const resolve = (p: ThemePref): Theme => (p === 'system' ? (media.matches ? 'dark' : 'light') : p)

function apply(p: ThemePref): Theme {
  const t = resolve(p)
  document.documentElement.classList.toggle('dark', t === 'dark')
  try {
    localStorage.setItem(THEME_KEY, p)
  } catch {
    /* trình duyệt chặn lưu — vẫn áp dụng cho phiên này */
  }
  return t
}

const initialPref = readPref()
const initialTheme = apply(initialPref)

// Menu ngang đã bỏ — chỉ còn menu dọc. Dọn khoá cũ cho sạch.
localStorage.removeItem('ecdp_nav_mode')

export const useUiStore = create<UiState>((set, get) => ({
  theme: initialTheme,
  themePref: initialPref,
  // Mặc định là cột icon (rail) như SaleSmartly; chỉ mở rộng khi người dùng đã chọn.
  sidebarCollapsed: localStorage.getItem('ecdp_sidebar') !== '0',
  toggleTheme: () => get().setThemePref(get().theme === 'dark' ? 'light' : 'dark'),
  setTheme: (t) => get().setThemePref(t),
  setThemePref: (p) => set({ themePref: p, theme: apply(p) }),
  toggleSidebar: () => get().setSidebarPinned(get().sidebarCollapsed),
  setSidebarPinned: (pinned) => {
    localStorage.setItem('ecdp_sidebar', pinned ? '0' : '1')
    set({ sidebarCollapsed: !pinned })
  },
  commandOpen: false,
  setCommandOpen: (open) => set({ commandOpen: open }),
}))

// Đang theo hệ thống mà máy đổi sáng/tối (vd. tự chuyển ban đêm) → đổi theo ngay.
media.addEventListener?.('change', () => {
  const { themePref, setThemePref } = useUiStore.getState()
  if (themePref === 'system') setThemePref('system')
})
